import React, { useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, ArrowRight, Clock, Calendar, BookOpen, Share2, 
  CheckCircle, AlertCircle, Brain, Users, Target, Shield,
  ChevronRight, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { PageLayout } from '../components/Layout';
import { SEO } from '../components/SEO';

// Article content data - rich SEO content based on RSSB documents
const articlesContent = {
  'understanding-train-driver-psychometric-tests': {
    title: 'Understanding Train Driver Psychometric Tests: The Complete Guide',
    metaDescription: 'Comprehensive guide to UK train driver psychometric tests. Learn about Group Bourdon, DTG, TRP tests, pass rates (20-40%), and how to prepare effectively. Based on RSSB research.',
    category: 'Guide',
    readTime: '12 min read',
    publishDate: 'December 2025',
    author: 'TDF Research Team',
    heroImage: '/psychometric-hero.jpg',
    content: `
## What Are Train Driver Psychometric Tests?

Train driver psychometric tests are standardised cognitive assessments used by UK Train Operating Companies (TOCs) to evaluate whether candidates possess the mental abilities required to safely operate passenger and freight trains. These tests form a critical part of the selection process and are **aligned to RIS-3751-TOM industry standards**.

According to research commissioned by the Rail Safety & Standards Board (RSSB), these assessments measure critical cognitive functions including:

- **Sustained attention** - ability to maintain focus over extended periods
- **Concentration under pressure** - performing accurately when stressed
- **Reaction time** - speed of response to visual and auditory stimuli
- **Memory recall** - retaining and applying procedural information
- **Spatial reasoning** - understanding track layouts and signals
- **Decision-making ability** - making safe choices under time pressure

## The Four-Stage Selection Process

UK train driver recruitment typically follows a four-stage process:

### Stage 1: Attracting Applicants
TOCs advertise positions and receive applications. The average number of applicants per vacancy is approximately **317 candidates**, making this a highly competitive career path.

### Stage 2: Sifting/Shortlisting
Initial screening of applications based on eligibility criteria. Up to **one-third of applicants** may withdraw or be filtered out at this stage.

### Stage 3: Assessment Centre
Candidates attend formal assessment centres where psychometric tests are administered. The average pass rate at assessment centres is around **41%**, though this varies by company.

### Stage 4: Final Selection
Remaining candidates undergo final interviews and checks. The assessment centre primarily serves to **exclude unsuitable candidates** rather than identify the best ones - most selection decisions happen before and after this stage.

## Key Psychometric Tests Used

### Group Bourdon Test (Concentration)
The Group Bourdon test assesses sustained attention and concentration. Candidates must identify specific dot patterns (typically groups of 4 dots) from a large array, testing their ability to maintain focus over time.

**Important findings from RSSB research:**
- Computerised and paper versions are **not equivalent**
- Omissions (missing targets) and Errors show correlation with job performance
- This test has **inconsistent validity** across different studies

### DTG (Reaction & Co-ordination Test)
This test measures reaction time and hand-eye coordination through tasks requiring quick, accurate responses to visual stimuli.

**Research findings:**
- Shows some correlation with safety-related behaviours
- Results are **mixed** across different validation studies
- May need upgrading or replacement based on RSSB recommendations

### TRP (Trainability for Rules & Procedures)
The TRP test assesses your ability to learn and apply rules - a critical skill for train drivers who must follow extensive operational procedures.

**Key insight:** This test **consistently correlates positively** with relevant outcomes including training success and safe performance. RSSB research recommends retaining this test.

### Criterion Based Interview (CBI)
While not technically a psychometric test, the CBI is a structured interview assessing competencies like communication and motivation. The failure rate for CBI criteria is only **5%**, partly because candidates have already been pre-selected.

## Pass Rates and Statistics

Understanding the difficulty level helps set realistic expectations:

| Metric | Statistic |
|--------|-----------|
| Average applicants per vacancy | 317 |
| Assessment centre pass rate | ~41% |
| First-time pass rate (industry estimate) | 20-40% |
| CBI failure rate | 5% |
| Assessment centre rejection rate | ~8% |

## Why Do Candidates Fail?

Research identifies several common reasons for failure:

1. **Unfamiliarity with test formats** - not knowing what to expect
2. **Poor time management** - not pacing responses correctly
3. **Inconsistent concentration** - performance drops during longer tests
4. **Underestimating difficulty** - arriving unprepared
5. **Test anxiety** - stress affecting performance

## What the Research Recommends

The RSSB review proposed significant updates to the selection process:

### Criteria to Assess
A comprehensive list of 11 criteria was recommended:
- Attention
- Vigilance  
- Train Handling Aptitude
- Decision Making
- Trainability
- Communication Skills
- Rule Compliance
- Conscientiousness
- Achievement Need
- Emotional Stability
- Tolerance of Working Alone

### Tests to Retain
- TRP (Trainability for Rules & Procedures)
- Motivation to follow rules assessment from CBI

### Tests to Update or Replace
- Computerised Group Bourdon
- DTG (Reaction & Co-ordination)

### Tests Potentially Removed
- Mechanical Comprehension Test (MT4.1) - validity inconsistent and only relevant for some companies

## How Practice Helps

While psychometric tests assess innate cognitive abilities, research consistently shows that **practice significantly improves performance**:

- Familiarity with test formats reduces anxiety
- Understanding time constraints improves pacing
- Knowing what to expect increases confidence
- Repeated practice builds relevant cognitive skills

Studies suggest candidates who practice can improve scores by **20-30%** on average.

## Preparing Effectively

Based on the research findings, here's how to prepare:

1. **Understand each test type** - know what's being measured
2. **Practice under timed conditions** - simulate real test pressure
3. **Focus on accuracy AND speed** - both matter
4. **Build sustained concentration** - practice longer sessions
5. **Get adequate rest** - cognitive performance depends on it
6. **Manage test anxiety** - familiarity reduces stress

## Key Takeaways

- Train driver psychometric tests are rigorous but **preparation helps significantly**
- The TRP test is the most valid predictor of success
- Pass rates of 20-40% first time are typical
- Assessment centres exclude unsuitable candidates - they don't pick the best
- Practice improves performance through familiarity and reduced anxiety

Ready to start your preparation? Our platform provides authentic practice tests aligned to the same cognitive abilities assessed in real train driver recruitment.
    `,
    relatedArticles: ['can-you-become-train-driver-at-18', 'mental-workload-train-drivers'],
    tags: ['Psychometric Tests', 'Selection Process', 'RSSB Research', 'Group Bourdon', 'Assessment Centre'],
    sources: [
      { title: 'RSSB T340 Report: Psychometric Testing Review', type: 'Research Report' },
      { title: 'RIS-3751-TOM Standards', type: 'Industry Standard' },
    ],
  },

  'can-you-become-train-driver-at-18': {
    title: 'Can You Become a Train Driver at 18? Age Requirements Explained',
    metaDescription: 'Learn about train driver minimum age requirements in the UK. RSSB research shows experience matters more than age. Find out how 18-year-olds can start their train driver career.',
    category: 'Career Advice',
    readTime: '8 min read',
    publishDate: 'December 2025',
    author: 'TDF Research Team',
    heroImage: '/psychometric-hero.jpg',
    content: `
## The Minimum Age Question

One of the most common questions from aspiring train drivers is: **"Can I become a train driver at 18?"**

The answer is increasingly **yes** - and there's substantial research backing this change.

## Current Age Requirements in the UK

Historically, the minimum age for train drivers in Great Britain was **20 years old**. However, following extensive research by the Rail Safety and Standards Board (RSSB), the industry has been moving towards allowing **18-year-olds** to begin their train driving careers.

The EU Train Driver Directive already allows issuing licences to applicants from **18 years old** for domestic operations, and GB rail is aligning with this approach.

## What the Research Shows

A comprehensive RSSB study examined whether reducing the minimum age from 20 to 18 would be safe. The findings were clear:

### Key Finding: Experience Trumps Age

The single most important finding was that **experience, rather than age, is the primary determinant of effective train driving performance**.

This means a well-trained 18-year-old with proper experience can perform just as safely as an older driver.

### Supporting Evidence

The research found:

- **No significant correlation** between younger drivers and SPAD (Signals Passed at Danger) rates
- **No age-related difference** in human performance categories among train drivers
- Characteristics associated with younger road drivers (like risk-taking) are **not applicable** to the train driver role due to its structured nature
- Multiple train safety systems provide adequate protection regardless of driver age

### Why Age 18 Works for Train Driving

The train driving role has characteristics that make it suitable for younger workers:

1. **Fixed timetables** - predictable, structured work
2. **Prescriptive rules** - clear guidelines to follow
3. **Limited improvisation** - unlike road driving, less judgment required
4. **Safety systems** - AWS, TPWS, and other protections
5. **Structured training** - comprehensive preparation before solo driving

## The Road Driving Comparison

You might wonder: if young road drivers have higher accident rates, why is train driving different?

The research explains this clearly. Young road drivers face issues like:
- Peer pressure in social driving situations
- Overconfidence in variable conditions
- Distractions from passengers
- Need for complex real-time decisions

**Train driving eliminates most of these factors:**
- Solitary cab environment
- Controlled conditions
- Clear procedural framework
- Limited decision variability
- Technology-assisted safety

## Benefits of Starting at 18

Lowering the minimum age offers several advantages:

### For Individuals
- **Earlier career start** - begin earning and progressing sooner
- **Apprenticeship opportunities** - straight from school pathway
- **Career security** - stable, well-paid profession from young age

### For the Industry
- **Addresses workforce aging** - current driver population skews older
- **Widens candidate pool** - more applicants to choose from
- **Encourages diversity** - attracts people for whom it's a first career
- **Apprenticeship integration** - links with education system

## What You Need at 18

If you're 18 and interested in becoming a train driver, here's what you'll need:

### Essential Requirements
- Meet medical fitness standards
- Pass psychometric assessments
- Complete comprehensive training programme
- Demonstrate required competencies

### Personal Qualities
- **Strong concentration** - ability to stay focused
- **Rule compliance** - comfortable following procedures
- **Emotional stability** - remain calm under pressure
- **Self-discipline** - work independently

### What You DON'T Need
- Previous driving experience (helpful but not required)
- Specific academic qualifications (varies by TOC)
- Railway background (complete training provided)

## The Selection Process for Young Candidates

The psychometric assessment process is the same regardless of age. You'll face:

1. **Application screening** - meeting basic criteria
2. **Psychometric tests** - cognitive ability assessments
3. **Medical examination** - fitness for safety-critical work
4. **Interviews** - competency-based questions
5. **Training programme** - if selected

### Pass/Fail Rates

Research shows that 18-year-olds perform comparably to older candidates in psychometric assessments. The key factors are:
- Preparation and practice
- Understanding test formats
- Managing test anxiety
- Natural cognitive abilities

## Tips for Young Applicants

Based on the research, here's advice for 18-year-olds applying:

### Preparation
- **Practice psychometric tests** - familiarity improves performance
- **Research the role thoroughly** - understand what you're signing up for
- **Develop concentration skills** - crucial for the tests and the job

### During Selection
- **Be honest about experience** - don't pretend to know things you don't
- **Show willingness to learn** - trainability is highly valued
- **Demonstrate maturity** - take the process seriously

### Managing Expectations
- **Understand shift patterns** - train driving involves unsocial hours
- **Consider work-life balance** - it's different from a 9-5 job
- **Prepare for responsibility** - you'll be in charge of passenger safety

## The Industry View

Train Operating Companies are increasingly open to younger candidates because:

- Research supports the safety of hiring 18-year-olds
- It helps address recruitment challenges
- Younger hires have longer potential careers
- Training can mould good habits from the start

## Key Takeaways

- **Yes, you can become a train driver at 18** - the industry is moving this direction
- **Experience matters more than age** - RSSB research confirms this
- **The selection process is the same** - you'll face identical assessments
- **Prepare thoroughly** - practice tests and understand the role
- **The role suits younger workers** - structured, rule-based environment

## Next Steps

If you're 18 (or approaching 18) and interested in train driving:

1. Start practicing psychometric tests now
2. Research TOCs recruiting in your area
3. Understand the commitment involved
4. Build your concentration and focus skills
5. Apply when positions are advertised

Our practice platform can help you prepare for the psychometric assessments you'll face, regardless of your age.
    `,
    relatedArticles: ['understanding-train-driver-psychometric-tests', 'neurodivergent-train-drivers-guide'],
    tags: ['Minimum Age', 'Career Entry', 'RSSB Research', 'Young Drivers', 'Apprenticeships'],
    sources: [
      { title: 'RSSB Minimum Driver Age Report', type: 'Research Report' },
      { title: 'EU Train Driver Directive', type: 'Regulation' },
    ],
  },

  'neurodivergent-train-drivers-guide': {
    title: 'Neurodivergent Train Drivers: Strengths, Challenges & Support',
    metaDescription: 'Guide for neurodivergent individuals considering train driving careers. Learn how ADHD, autism, and dyslexia traits can be strengths. 15% of the UK is neurodivergent.',
    category: 'Inclusion',
    readTime: '10 min read',
    publishDate: 'December 2025',
    author: 'TDF Research Team',
    heroImage: '/psychometric-hero.jpg',
    content: `
## Understanding Neurodiversity in Train Driving

An estimated **15% of the UK population is neurodivergent**, meaning many individuals within the rail industry - including train drivers - may identify as having conditions like autism, ADHD, dyslexia, or dyspraxia.

Recent research by the Rail Safety and Standards Board (RSSB) has examined how neurodivergent individuals can thrive as train drivers, identifying both the strengths they bring and the support that helps them succeed.

## What is Neurodiversity?

Neurodiversity is an umbrella term covering conditions that affect how the brain processes information. Common neurodivergent conditions include:

- **Autism Spectrum Condition (ASC)** - differences in social interaction and sensory processing
- **ADHD** - differences in attention, focus, and impulse control  
- **Dyslexia** - differences in reading and written language processing
- **Dyspraxia** - differences in motor coordination and planning
- **Tourette Syndrome** - presence of tics

Importantly, neurodiversity exists on a **spectrum** - every individual's experience is unique.

## Why Train Driving May Suit Neurodivergent Individuals

The RSSB research found that several aspects of the train driver role align well with neurodivergent strengths:

### Structured Environment
- **Fixed timetables** - predictable schedules reduce uncertainty
- **Prescriptive rules** - clear guidelines rather than ambiguous expectations
- **Defined procedures** - knowing exactly what's required

### Solitary Work
- **Limited social interaction** in the cab
- **Reduced sensory overload** from colleagues
- **Independence** in carrying out duties

### Rule-Based Tasks
- **Clear right/wrong actions** - procedural compliance
- **Minimal ambiguity** - instructions are specific
- **Pattern-based work** - routines and signals

### Technical Focus
- **Attention to detail** is valued and rewarded
- **Technical knowledge** - systematic learning
- **Consistency** - same tasks repeated reliably

## Strengths Neurodivergent Drivers Bring

Research identified numerous strengths that neurodivergent individuals often bring to the train driver role:

### Attention to Detail
Many neurodivergent individuals notice details others miss - crucial for spotting:
- Track defects
- Signal irregularities  
- Infrastructure issues
- Operational anomalies

### Hyperfocus
The ability to concentrate intensely on a single task is valuable for:
- Long route sections
- Monitoring instruments
- Maintaining vigilance

### Pattern Recognition
Strong ability to identify patterns helps with:
- Learning routes
- Recognising signal aspects
- Detecting changes in normal operations

### Rule Compliance
Many neurodivergent individuals are naturally inclined to:
- Follow procedures exactly
- Maintain consistency
- Respect rules and structures

### Conscientiousness
Tendency towards thoroughness and perfectionism supports:
- Safety compliance
- Pre-departure checks
- Accurate record keeping

### Spatial Memory
Strong visual-spatial abilities assist with:
- Route learning
- Track layout understanding
- Signal positioning awareness

### Heightened Sensory Perception
Some neurodivergent individuals have enhanced sensory awareness, helping detect:
- Unusual sounds indicating mechanical issues
- Visual anomalies on the track
- Changes in train handling

## Potential Challenges and Support

The research also identified challenges neurodivergent drivers may face, along with support strategies:

### Sensory Processing
**Challenge:** Overwhelming stimuli (bright lights, loud noises)
**Support:** 
- Noise-reducing headsets where permitted
- Sunglasses or tinted visors
- Sensory breaks during shifts

### Focus and Attention
**Challenge:** Either hyperfocus (missing other inputs) or difficulty sustaining attention
**Support:**
- Structured task checklists
- Clear communication protocols
- Technology aids for monitoring

### Change and Routine
**Challenge:** Difficulty adapting to schedule changes or disruptions
**Support:**
- Advance notice of changes where possible
- Clear communication of altered arrangements
- Familiar backup procedures

### Communication Differences
**Challenge:** Literal interpretation of language, difficulty with ambiguous instructions
**Support:**
- Clear, direct communication
- Written confirmation of verbal instructions
- Explicit rather than implied expectations

### Social Interaction
**Challenge:** Difficulty reading social cues or managing workplace relationships
**Support:**
- Understanding managers
- Clear feedback mechanisms
- Peer support networks

### Reading and Writing
**Challenge:** Difficulty with written assessments or documentation
**Support:**
- Alternative assessment formats
- Extra time for written tasks
- Text-to-speech technology

## Preparing for Psychometric Tests

If you're neurodivergent and preparing for train driver psychometric tests, consider these approaches:

### Request Reasonable Adjustments
You may be entitled to:
- Extra time
- Separate testing room
- Modified test formats
- Breaks during assessment

### Practice Extensively
Familiarity reduces anxiety and improves performance:
- Use practice tests repeatedly
- Simulate test conditions
- Build concentration gradually

### Leverage Your Strengths
Many psychometric tests actually measure skills where neurodivergent individuals excel:
- **Attention tests** - hyperfocus can be advantageous
- **Pattern recognition** - natural strength for many
- **Rule-based tasks** - systematic thinking helps

### Manage Sensory Environment
For the actual assessment:
- Wear comfortable clothing
- Bring permitted sensory aids
- Choose optimal seating if possible

## Disclosure: Your Choice

Whether to disclose neurodivergent status is a personal decision. Consider:

### Reasons to Disclose
- Access to reasonable adjustments
- Understanding from employers
- Legal protections under Equality Act
- Tailored support and training

### Concerns About Disclosure
- Fear of discrimination
- Privacy preferences
- Stigma concerns
- Uncertainty about reactions

### The Law
The Equality Act 2010 protects neurodivergent individuals from discrimination. Employers must make reasonable adjustments for disabled employees.

## What TOCs Are Doing

Rail companies are increasingly focusing on neurodiversity inclusion:

- **Awareness training** for managers
- **Clearer adjustment processes**
- **Support networks** for neurodivergent staff
- **Recruitment process reviews** for accessibility

## Key Statistics

- **15%** of UK population is neurodivergent
- Many successful train drivers are neurodivergent (diagnosed or undiagnosed)
- Structured roles have **lower barriers** for neurodivergent workers
- Properly supported neurodivergent employees have **equivalent performance**

## Key Takeaways

- **Train driving can suit neurodivergent individuals** - the structured, rule-based nature aligns with many strengths
- **You bring valuable qualities** - attention to detail, focus, pattern recognition
- **Support is available** - reasonable adjustments are a legal right
- **Disclosure is your choice** - weigh the benefits for your situation
- **Prepare for assessments** - practice and familiarity help everyone

## Next Steps

If you're neurodivergent and considering train driving:

1. Research the role thoroughly
2. Identify your personal strengths and challenges
3. Practice psychometric tests extensively
4. Consider what adjustments might help you
5. Connect with neurodiversity support organisations

Our practice tests allow you to prepare at your own pace, building familiarity and confidence before the real assessment.
    `,
    relatedArticles: ['understanding-train-driver-psychometric-tests', 'can-you-become-train-driver-at-18'],
    tags: ['Neurodiversity', 'ADHD', 'Autism', 'Dyslexia', 'Inclusion', 'Accessibility'],
    sources: [
      { title: 'RSSB: Driving Inclusion - Understanding and Supporting Neurodivergent Train Drivers', type: 'Research Report' },
      { title: 'Equality Act 2010', type: 'Legislation' },
    ],
  },

  'mental-workload-train-drivers': {
    title: 'Mental Workload in Train Driving: What Candidates Should Know',
    metaDescription: 'Understanding cognitive demands of train driving and why psychometric tests assess these skills. Learn about attention, vigilance, and decision-making requirements.',
    category: 'Research',
    readTime: '7 min read',
    publishDate: 'December 2025',
    author: 'TDF Research Team',
    heroImage: '/psychometric-hero.jpg',
    content: `
## What is Mental Workload?

Mental workload refers to the cognitive demands placed on a person while performing a task. For train drivers, understanding mental workload is crucial because:

- **Too high** workload leads to errors, stress, and safety risks
- **Too low** workload causes boredom, reduced vigilance, and missed signals
- **Optimal** workload maintains alertness and safe performance

The Rail Safety and Standards Board (RSSB) has developed specific tools to assess train driver mental workload, recognising its importance for safety and performance.

## Why Mental Workload Matters for Candidates

If you're preparing for train driver selection, understanding mental workload helps you:

1. **Understand test purpose** - psychometric tests assess your ability to handle cognitive demands
2. **Prepare effectively** - practice the skills that matter
3. **Set expectations** - know what the job requires mentally
4. **Assess fit** - determine if the role suits your cognitive style

## The Cognitive Demands of Train Driving

Train driving involves multiple cognitive functions working together:

### Sustained Attention
- Monitoring signals, track, and instruments continuously
- Maintaining focus over long shifts (often 8+ hours)
- Remaining alert during monotonous sections

### Divided Attention
- Watching the track ahead
- Monitoring instruments
- Listening for radio communications
- Being aware of passengers/platform activity

### Vigilance
- Detecting infrequent but important events
- Responding to unexpected hazards
- Maintaining readiness despite routine conditions

### Decision Making
- Interpreting signal aspects
- Responding to degraded conditions
- Prioritising actions during incidents

### Memory
- Remembering route knowledge
- Recalling procedures
- Applying rules correctly

### Perception
- Reading signals accurately
- Judging distances and speeds
- Identifying hazards quickly

## How Psychometric Tests Measure These Skills

Each type of psychometric test targets specific cognitive abilities:

### Concentration Tests (e.g., Group Bourdon)
**Measures:** Sustained attention, visual scanning
**Relevance:** Signal monitoring, track observation

### Reaction Time Tests
**Measures:** Speed of response, alertness
**Relevance:** Emergency braking, hazard response

### Vigilance Tests
**Measures:** Ability to detect rare events over time
**Relevance:** Spotting unexpected hazards, signal changes

### Memory Tests
**Measures:** Working memory, recall ability
**Relevance:** Route knowledge, procedure application

### Multi-tasking Tests
**Measures:** Divided attention, task switching
**Relevance:** Handling multiple information sources

## Mental Workload Challenges

Research identifies several workload-related challenges in train driving:

### High Workload Scenarios
- Approaching complex junctions
- Operating in degraded conditions
- Managing delays and disruptions
- Unfamiliar routes or diversions

### Low Workload Scenarios
- Long, straight track sections
- Quiet overnight shifts
- Highly automated routes
- Repetitive suburban services

### The "Vigilance Paradox"
One of the biggest challenges: staying alert when nothing is happening. This is why vigilance tests are crucial - they assess your ability to maintain attention during boring periods.

## What the Research Found

RSSB research on mental workload identified key factors affecting train driver cognitive load:

### Environmental Factors
- Weather conditions
- Lighting (day/night)
- Noise levels
- Cab ergonomics

### Infrastructure Factors
- Signal spacing
- Track complexity
- Speed variations
- Station frequency

### Job Design Factors
- Shift length
- Route familiarity
- Communication demands
- Procedure complexity

### Journey Factors
- Passenger loading
- Time pressure
- Incident response requirements
- Disruption management

## Preparing for Cognitive Assessment

Understanding mental workload helps you prepare effectively:

### Build Concentration Stamina
- Practice focused tasks for increasing durations
- Start with 10-15 minutes, build to 30+
- Take short breaks, then continue

### Train Vigilance
- Practice monitoring tasks where events are rare
- Resist the urge to zone out
- Stay engaged even when bored

### Develop Multi-tasking
- Practice doing two things at once
- Start simple, increase complexity
- Maintain accuracy under pressure

### Manage Cognitive Load
- Learn to prioritise information
- Practice filtering irrelevant input
- Focus on what matters most

## The Optimal Mindset

For both tests and the actual job, the optimal approach involves:

### Consistent Effort
Not maximum effort all the time, but **steady, sustainable performance** over extended periods.

### Active Monitoring
Not passive observation, but **engaged scanning** with purpose and attention.

### Appropriate Caution
Not paranoid anxiety, but **calibrated alertness** to genuine risks.

### Calm Under Pressure
Not panicked reaction, but **measured response** when things go wrong.

## Key Takeaways

- **Mental workload is central** to train driving - tests assess this for good reason
- **Both high and low workload** pose challenges - you need to handle both
- **Vigilance is crucial** - maintaining attention when nothing happens is hard
- **Practice builds capacity** - cognitive skills can be trained
- **Understand the purpose** - knowing why tests exist helps you prepare

## Practical Next Steps

1. **Practice concentration tests** - build your sustained attention
2. **Try vigilance exercises** - monitor for rare events
3. **Develop multi-tasking** - handle multiple inputs
4. **Build stamina** - extend your focused practice duration
5. **Understand your limits** - know when you need breaks

Our practice platform includes tests designed to assess and build the cognitive skills required for train driving.
    `,
    relatedArticles: ['understanding-train-driver-psychometric-tests', 'neurodivergent-train-drivers-guide'],
    tags: ['Mental Workload', 'Cognitive Skills', 'Safety', 'Vigilance', 'Attention'],
    sources: [
      { title: 'RSSB T147 Report: Tools for Train Driver Mental Workload Assessment', type: 'Research Report' },
    ],
  },

  'ris-3751-tom-explained': {
    title: 'RIS-3751-TOM Explained: The Official Train Driver Assessment Standard',
    metaDescription: 'Complete guide to RIS-3751-TOM Issue 4 - the official UK standard for train driver psychometric assessments. Learn about all 9 aptitude categories, test types, attempt rules, and what TOCs must follow.',
    category: 'Official Standard',
    readTime: '15 min read',
    publishDate: 'December 2025',
    author: 'TDF Research Team',
    heroImage: '/psychometric-hero.jpg',
    content: `
## What is RIS-3751-TOM?

**RIS-3751-TOM** is the Rail Industry Standard that governs psychometric assessments for train driver candidates across the GB mainline railway. Published by the Rail Safety and Standards Board (RSSB), this document defines exactly what Train Operating Companies (TOCs) must assess and how they must do it.

The current version is **Issue Four (December 2024)**, and understanding this standard gives you a significant advantage in your preparation - you'll know exactly what's being tested and why.

## Why This Standard Exists

The purpose of RIS-3751-TOM is to:

- **Standardise assessments** across all UK Train Operating Companies
- **Ensure safety** by identifying candidates with the necessary aptitudes
- **Provide fairness** through validated, reliable testing methods
- **Protect candidates** with clear rules on attempts, validity, and reasonable adjustments

The standard is designed to help TOCs **deselect candidates who don't meet minimum requirements** for safe train operation - it's about ensuring everyone who passes can do the job safely.

## The 9 Aptitude Categories Assessed

RIS-3751-TOM defines **nine specific aptitudes** that every train driver candidate must be assessed on. These are the exact abilities the psychometric tests measure:

### 1. Selective Attention
**What it means:** Differentiating between relevant and irrelevant information and attending selectively to what matters.

**Real-world example:** Distinguishing important alarms from background noise, or focusing on the correct signal among many.

**How it's tested:** Tests like the Paper Group Bourdon, where you identify specific patterns while ignoring others.

### 2. Divided Attention
**What it means:** Switching attention between different information sources and performing tasks in parallel.

**Real-world example:** Monitoring lineside information while also checking in-cab instruments and listening for radio communications.

**How it's tested:** The TEA-Occ (Test of Everyday Attention - Occupational) assesses your ability to handle multiple inputs simultaneously.

### 3. Vigilance
**What it means:** Attending to and responding appropriately to infrequent stimuli over extended periods.

**Real-world example:** Staying alert for unexpected hazards during long, routine stretches of track where nothing usually happens.

**How it's tested:** The WAFV Vigilance test measures how well you maintain attention and detect rare events over time.

### 4. Memory
**What it means:** Learning, recalling, and correctly applying job-related information within required time limits.

**Real-world example:** Remembering route details, speed restrictions, and operational procedures.

**How it's tested:** TRP1/TRP2 (Trainability for Rules and Procedures) tests assess your ability to learn and apply rules accurately.

### 5. Reasoning
**What it means:** Solving problems, making decisions, and interpreting information correctly.

**Real-world example:** Diagnosing faults, deciding the correct response to degraded conditions, or interpreting complex signal arrangements.

**How it's tested:** Reasoning assessments and situational judgement exercises evaluate your decision-making ability.

### 6. Perception
**What it means:** Anticipating elements in the traffic environment and making correct decisions based on relative speed and distance.

**Real-world example:** Judging stopping distances, reading signals at speed, and anticipating track conditions.

**How it's tested:** The ATAVT (Adaptive Tachistoscopic Traffic Test) presents brief images and tests your ability to perceive and recall details.

### 7. Reaction Time
**What it means:** Making quick and adequate responses to simple or complex stimuli, with quality of performance (not just speed) considered.

**Real-world example:** Responding quickly to emergency situations, hazards, or unexpected events.

**How it's tested:** Computerised reaction time tests measure both speed and accuracy of your responses.

### 8. Hand Coordination
**What it means:** Making appropriate and controlled movements in response to complex stimuli.

**Real-world example:** Operating train controls smoothly, making precise adjustments to speed and braking.

**How it's tested:** The 2HAND Two Hand Coordination test assesses your motor control and coordination abilities.

### 9. Communication
**What it means:** Listening to, understanding, responding to, and conveying information clearly - both orally and in writing.

**Real-world example:** Receiving and acknowledging instructions from signallers, communicating with passengers during incidents.

**How it's tested:** Multi-Modal Interview (MMI) and written exercises assess your communication abilities.

## Behavioural Assessment

In addition to the cognitive tests, RIS-3751-TOM requires assessment of **behavioural aptitudes** - your general tendencies, personality factors, and suitability for the role. This is typically assessed through:

- Structured interviews
- Situational Judgement Exercises (SJE)
- Personality questionnaires

The standard emphasises looking for an **absence of deficiencies** rather than specific personality types.

## Official Test Methods

The standard specifies particular assessment tools that have been validated for train driver selection:

| Aptitude | Assessment Method |
|----------|-------------------|
| Selective Attention | Paper Group Bourdon |
| Divided Attention | TEA-Occ |
| Vigilance | WAFV Vigilance Test |
| Memory/Trainability | TRP1/TRP2 |
| Perception | ATAVT |
| Hand Coordination | 2HAND Test |
| Communication | Multi-Modal Interview |
| Behavioural | SJE, Structured Interview |

## The Rules on Attempts

RIS-3751-TOM sets clear rules about how many times you can take the assessment:

### Maximum Attempts: 3
You are allowed a **maximum of three attempts** at the psychometric assessment. After three failures, you cannot reattempt.

### Waiting Periods
There are mandatory waiting periods between attempts to ensure fairness and prevent "practice effects":

- **After 1st failure:** Must wait before retaking
- **After 2nd failure:** Longer waiting period required
- **After 3rd failure:** No further attempts permitted

### Why These Rules Exist
- To give candidates time to genuinely develop abilities
- To prevent rote learning of specific test items
- To ensure the assessment measures actual aptitude, not test familiarity

## Validity of Results

Your psychometric assessment results are **valid for five years** from the date of assessment. This means:

- If you pass but don't immediately get a position, you may not need to retake
- Results can be transferred between TOCs within the validity period
- After five years, a new assessment is required

## Reasonable Adjustments for Neurodiversity

RIS-3751-TOM requires TOCs to have policies for supporting neurodiverse candidates. The standard mandates:

### What TOCs Must Do
- Have a clear neurodiversity policy
- Communicate support options to candidates
- Make reasonable adjustments where appropriate
- Ensure adjustments don't compromise safety-critical requirements

### Types of Adjustments
- Extra time for assessments
- Separate testing rooms
- Breaks during testing
- Alternative formats where possible
- Clear, explicit instructions

### Important Note
Adjustments are about **removing barriers** to demonstrate your abilities - they don't lower the standard required for safety-critical aspects of the role.

## Transfer of Safety Information

If you have **previous train driving experience**, there's a formal process for transferring your information:

### What's Transferred
- Previous assessment results
- Training records
- Any safety-related incidents
- Licence information

### Why It Matters
This ensures TOCs have complete information about your background before hiring you for a safety-critical role.

## Quality Assurance

RIS-3751-TOM requires rigorous quality control:

### Annual Audits
- Alternating between internal and external audits
- Checking compliance with the standard
- Reviewing assessment delivery and results

### Qualified Assessors
- Must be qualified by the British Psychological Society (BPS)
- Must have relevant test publisher qualifications
- Regular updates and validation required

## What This Means for Your Preparation

Understanding RIS-3751-TOM helps you prepare effectively:

### Know What's Tested
The nine aptitude categories tell you exactly what abilities to develop:
- Attention (selective and divided)
- Vigilance
- Memory
- Reasoning
- Perception
- Reaction time
- Hand coordination
- Communication

### Understand the Format
The specified tests (Bourdon, TEA-Occ, WAFV, etc.) have particular formats - practicing similar exercises helps you perform better.

### Respect the Process
The three-attempt limit and waiting periods mean you should only attempt the real assessment when genuinely ready.

### Request Adjustments If Needed
If you're neurodivergent, know that you have the right to request reasonable adjustments - use them.

## Key Takeaways

- **RIS-3751-TOM is THE standard** - all UK TOCs must follow it
- **Nine aptitudes are assessed** - know them and practice them
- **Maximum three attempts** - make each one count
- **Results valid for five years** - plan your applications accordingly
- **Reasonable adjustments available** - request them if you need them
- **Practice matters** - familiarity with test formats improves performance

## Prepare with Confidence

Our practice platform is aligned to RIS-3751-TOM, covering all nine aptitude categories with exercises designed to build the exact abilities the official tests measure. Start practicing today and approach your assessment with confidence.
    `,
    relatedArticles: ['understanding-train-driver-psychometric-tests', 'neurodivergent-train-drivers-guide'],
    tags: ['RIS-3751-TOM', 'Official Standard', 'RSSB', 'Assessment Requirements', 'Aptitudes', 'Test Rules'],
    sources: [
      { title: 'RIS-3751-TOM Issue 4 (December 2024)', type: 'Rail Industry Standard' },
      { title: 'Rail Safety and Standards Board (RSSB)', type: 'Regulatory Body' },
    ],
  },

  'train-driver-assessment-attempt-rules': {
    title: 'Train Driver Assessment Attempt Rules: What Happens If You Fail?',
    metaDescription: 'Official rules on train driver psychometric test attempts. Learn about the 3-attempt maximum, waiting periods between tests, and what to do if you fail. Based on RIS-3751-TOM.',
    category: 'Rules & Regulations',
    readTime: '6 min read',
    publishDate: 'December 2025',
    author: 'TDF Research Team',
    heroImage: '/psychometric-hero.jpg',
    content: `
## Understanding Attempt Rules

One of the most common questions from aspiring train drivers is: **"What happens if I fail the psychometric tests?"** The rules are clearly defined in RIS-3751-TOM, the official industry standard.

## The Three-Attempt Rule

The official standard states that candidates are allowed a **maximum of three attempts** at the psychometric assessment.

### What This Means
- You have up to three chances to pass
- After three failures, you cannot reattempt the assessment
- This rule applies across all Train Operating Companies
- Results are tracked industry-wide

### Why Only Three Attempts?
The limit exists to:
- Ensure assessments measure genuine aptitude, not test familiarity
- Prevent candidates from simply memorising test patterns
- Maintain the validity and reliability of the selection process
- Ensure safety standards are upheld

## Waiting Periods Between Attempts

If you fail, you must wait before trying again. RIS-3751-TOM requires significant time intervals between attempts:

### Purpose of Waiting Periods
- Allow time for genuine development of abilities
- Prevent "practice effects" from recent exposure
- Ensure each attempt is a fair assessment
- Give candidates time to prepare properly

### Typical Waiting Times
While specific periods vary by TOC, expect:
- Several months between first and second attempt
- Longer gap before a third attempt
- Some TOCs require 6-12 months between attempts

## What Happens After Each Outcome

### After Your First Attempt

**If You Pass:**
- Your results are valid for five years
- You can proceed with the rest of the selection process
- Results may be transferable to other TOCs

**If You Fail:**
- Required waiting period before retaking
- Use the time to practice and prepare
- Consider getting feedback if available
- Don't rush into a second attempt unprepared

### After Your Second Attempt

**If You Pass:**
- Proceed as normal
- Results valid for five years

**If You Fail:**
- This is your final chance
- Longer waiting period usually required
- Seriously assess your preparation strategy
- Consider whether the role is right for you

### After Your Third Attempt

**If You Pass:**
- You've made it - proceed with selection

**If You Fail:**
- No further attempts permitted
- You cannot become a train driver through this pathway
- Consider related roles in the rail industry

## Do Results Transfer Between TOCs?

Yes, with important conditions:

### Within Validity Period
- Results are generally valid for five years
- Can be transferred between Train Operating Companies
- TOCs may request your previous results

### Failed Attempts
- Failed attempts are also recorded
- TOCs can access your assessment history
- You cannot "reset" your attempt count by applying elsewhere

### Important Implication
**Don't waste attempts** by applying to multiple TOCs simultaneously and failing repeatedly - each attempt counts toward your total.

## How to Make Each Attempt Count

Given the limited attempts, preparation is crucial:

### Before Your First Attempt
1. **Practice extensively** - use all available preparation resources
2. **Understand the tests** - know what each assessment measures
3. **Build the skills** - work on attention, memory, reaction time
4. **Be realistic** - only attempt when genuinely ready
5. **Get rest** - arrive at the assessment well-rested

### If You Need to Retake
1. **Analyse what went wrong** - which tests were difficult?
2. **Target weak areas** - focus practice on problem areas
3. **Use the waiting period** - genuinely develop abilities
4. **Consider professional support** - coaching or preparation courses
5. **Don't rush** - wait until you're truly prepared

## Reasonable Adjustments Don't Affect Attempts

If you receive reasonable adjustments (for neurodiversity or disability), this doesn't change the attempt rules:

- You still have maximum three attempts
- Adjustments help you demonstrate your abilities
- The same standards apply for safety-critical aspects
- Adjustments are about fairness, not lowering requirements

## Special Circumstances

### Mitigating Circumstances
RIS-3751-TOM requires TOCs to have policies for unexpected events during assessments. If something significant happens (illness, emergency, technical failure), you may be able to:

- Have the attempt discounted
- Reschedule without using an attempt
- Request special consideration

**Note:** This is at the TOC's discretion and requires evidence of genuine mitigating circumstances.

### Previous Experience
If you have prior train driving experience, there's a transfer process for your safety information. Previous employers must provide details about your history, which may affect how your current application is assessed.

## Key Takeaways

- **Maximum 3 attempts** - this is industry-wide and tracked
- **Waiting periods required** - use them to prepare properly
- **Results last 5 years** - plan your applications strategically
- **Attempts count everywhere** - you can't reset by switching TOCs
- **Prepare thoroughly** - make each attempt count
- **Use mitigating circumstances** - if genuine issues occur

## Your Best Strategy

Given these rules, the smartest approach is:

1. **Prepare extensively before your first attempt**
2. **Only apply when you're genuinely ready**
3. **Use practice tests to assess your readiness**
4. **Don't waste attempts** - one well-prepared attempt is better than three rushed ones
5. **If you fail, use the waiting period productively**

Our platform helps you prepare thoroughly so you can approach your assessment with confidence and make your attempts count.
    `,
    relatedArticles: ['ris-3751-tom-explained', 'understanding-train-driver-psychometric-tests'],
    tags: ['Attempt Rules', 'Retakes', 'RIS-3751-TOM', 'Assessment Policy', 'What If You Fail'],
    sources: [
      { title: 'RIS-3751-TOM Issue 4 (December 2024)', type: 'Rail Industry Standard' },
    ],
  },
};

// Markdown-like content renderer
const ContentRenderer = ({ content }) => {
  const lines = content.trim().split('\n');
  const elements = [];
  let inList = false;
  let listItems = [];
  let inTable = false;
  let tableRows = [];
  let tableHeaders = [];

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <ul key={`list-${elements.length}`} className="list-disc list-inside space-y-2 text-[#334155] mb-6 ml-4">
          {listItems.map((item, i) => (
            <li key={i} className="leading-relaxed">{item}</li>
          ))}
        </ul>
      );
      listItems = [];
    }
    inList = false;
  };

  const flushTable = () => {
    if (tableRows.length > 0) {
      elements.push(
        <div key={`table-${elements.length}`} className="overflow-x-auto mb-6">
          <table className="min-w-full border border-slate-200 rounded-lg">
            <thead className="bg-[#0B1D51]/5">
              <tr>
                {tableHeaders.map((header, i) => (
                  <th key={i} className="px-4 py-3 text-left text-sm font-semibold text-[#0B1D51] border-b">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableRows.map((row, i) => (
                <tr key={i} className="border-b last:border-0">
                  {row.map((cell, j) => (
                    <td key={j} className="px-4 py-3 text-sm text-[#334155]">{cell}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      tableRows = [];
      tableHeaders = [];
    }
    inTable = false;
  };

  lines.forEach((line, index) => {
    const trimmedLine = line.trim();

    // Skip empty lines
    if (!trimmedLine) {
      flushList();
      flushTable();
      return;
    }

    // Table handling
    if (trimmedLine.startsWith('|') && trimmedLine.endsWith('|')) {
      flushList();
      const cells = trimmedLine.split('|').filter(c => c.trim()).map(c => c.trim());
      
      if (trimmedLine.includes('---')) {
        // This is the separator row, skip it
        return;
      }
      
      if (!inTable) {
        inTable = true;
        tableHeaders = cells;
      } else {
        tableRows.push(cells);
      }
      return;
    } else if (inTable) {
      flushTable();
    }

    // H2 headers
    if (trimmedLine.startsWith('## ')) {
      flushList();
      elements.push(
        <h2 key={index} className="font-heading text-2xl font-bold text-[#0B1D51] mt-10 mb-4">
          {trimmedLine.replace('## ', '')}
        </h2>
      );
      return;
    }

    // H3 headers
    if (trimmedLine.startsWith('### ')) {
      flushList();
      elements.push(
        <h3 key={index} className="font-heading text-xl font-semibold text-[#0B1D51] mt-8 mb-3">
          {trimmedLine.replace('### ', '')}
        </h3>
      );
      return;
    }

    // List items
    if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
      inList = true;
      const text = trimmedLine.replace(/^[-*] /, '');
      // Handle bold text
      const formattedText = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      listItems.push(<span dangerouslySetInnerHTML={{ __html: formattedText }} />);
      return;
    } else if (inList) {
      flushList();
    }

    // Numbered list items
    if (/^\d+\.\s/.test(trimmedLine)) {
      flushList();
      const text = trimmedLine.replace(/^\d+\.\s/, '');
      const formattedText = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      elements.push(
        <p key={index} className="text-[#334155] mb-2 ml-4 flex items-start gap-2">
          <span className="font-semibold text-[#0B1D51]">{trimmedLine.match(/^\d+/)[0]}.</span>
          <span dangerouslySetInnerHTML={{ __html: formattedText }} />
        </p>
      );
      return;
    }

    // Regular paragraphs
    const formattedText = trimmedLine
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>');
    
    elements.push(
      <p key={index} className="text-[#334155] leading-relaxed mb-4" dangerouslySetInnerHTML={{ __html: formattedText }} />
    );
  });

  flushList();
  flushTable();

  return <>{elements}</>;
};

export const ArticlePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const article = articlesContent[slug];

  // Add JSON-LD structured data
  useEffect(() => {
    if (!article) return;

    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": article.title,
      "description": article.metaDescription,
      "author": {
        "@type": "Organization",
        "name": "Train Driver Foundation"
      },
      "publisher": {
        "@type": "Organization",
        "name": "TDF Psychometrics",
        "logo": {
          "@type": "ImageObject",
          "url": "https://traindriverpsychometrics.co.uk/TDF.png"
        }
      },
      "datePublished": "2025-12-01",
      "dateModified": "2025-12-01"
    };

    const script = document.createElement('script');
    script.id = 'article-schema';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(articleSchema);
    document.head.appendChild(script);

    return () => {
      const existingScript = document.getElementById('article-schema');
      if (existingScript) existingScript.remove();
    };
  }, [article]);

  if (!article) {
    return (
      <PageLayout>
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-bold text-[#0B1D51] mb-4">Article Not Found</h1>
          <p className="text-[#64748B] mb-8">The article you're looking for doesn't exist.</p>
          <Link to="/resources">
            <Button>Back to Resources</Button>
          </Link>
        </div>
      </PageLayout>
    );
  }

  const relatedArticles = article.relatedArticles
    ?.map(slug => articlesContent[slug])
    .filter(Boolean) || [];

  return (
    <PageLayout>
      <SEO 
        title={article.title}
        description={article.metaDescription}
        canonical={`/resources/${slug}`}
      />

      {/* Article Header */}
      <article>
        <header className="bg-gradient-to-b from-[#0B1D51] to-[#1a3a7a] py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Link 
                to="/resources" 
                className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-6 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Resources
              </Link>
              
              <div className="flex items-center gap-3 mb-4">
                <Badge className="bg-[#FF9F1C] text-white">
                  {article.category}
                </Badge>
                <span className="text-white/70 text-sm flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {article.readTime}
                </span>
              </div>
              
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
                {article.title}
              </h1>
              
              <p className="text-lg text-white/80 mb-6">
                {article.metaDescription}
              </p>
              
              <div className="flex items-center gap-4 text-white/60 text-sm">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {article.publishDate}
                </span>
                <span>By {article.author}</span>
              </div>
            </motion.div>
          </div>
        </header>

        {/* Article Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="prose prose-lg max-w-none"
          >
            <ContentRenderer content={article.content} />
          </motion.div>

          {/* Tags */}
          <div className="mt-12 pt-8 border-t border-slate-200">
            <h4 className="text-sm font-semibold text-[#64748B] mb-3">Topics covered:</h4>
            <div className="flex flex-wrap gap-2">
              {article.tags.map((tag) => (
                <Badge key={tag} variant="outline" className="text-[#64748B]">
                  {tag}
                </Badge>
              ))}
            </div>
          </div>

          {/* Sources */}
          {article.sources && (
            <div className="mt-8 p-6 bg-[#F8FAFC] rounded-lg">
              <h4 className="font-semibold text-[#0B1D51] mb-3 flex items-center gap-2">
                <BookOpen className="w-4 h-4" />
                Research Sources
              </h4>
              <ul className="space-y-2">
                {article.sources.map((source, i) => (
                  <li key={i} className="text-sm text-[#64748B]">
                    <span className="font-medium text-[#334155]">{source.title}</span>
                    <span className="ml-2">({source.type})</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="bg-[#F8FAFC] py-12">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <h3 className="font-heading text-xl font-bold text-[#0B1D51] mb-6">
                Related Articles
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                {relatedArticles.map((related) => (
                  <Link 
                    key={related.title} 
                    to={`/resources/${article.relatedArticles.find(s => articlesContent[s]?.title === related.title)}`}
                  >
                    <Card className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <Badge variant="outline" className="text-xs mb-2">
                          {related.category}
                        </Badge>
                        <h4 className="font-semibold text-[#0B1D51] hover:text-[#FF9F1C] transition-colors">
                          {related.title}
                        </h4>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="py-12 bg-[#0B1D51]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h3 className="font-heading text-2xl font-bold text-white mb-4">
              Ready to Start Practicing?
            </h3>
            <p className="text-white/80 mb-6">
              Put this knowledge into action with our authentic psychometric test simulations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" className="bg-[#FF9F1C] hover:bg-[#FF9F1C]/90 text-white">
                  Start Free Practice
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/tests">
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10">
                  View All Tests
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </article>
    </PageLayout>
  );
};

export default ArticlePage;
